<template>
  <main class="site-page relative min-h-screen overflow-hidden bg-[var(--page)] text-[var(--text)] antialiased"
    :style="{ '--pointer-x': `${pointer.x}%`, '--pointer-y': `${pointer.y}%` }" @pointermove="handlePointerMove">
    <div class="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
      <div class="ambient-grid absolute inset-0"></div>
      <div class="ambient-noise absolute inset-0"></div>
      <div class="pointer-glow absolute inset-0"></div>
      <div class="ambient-orb ambient-orb-one"></div>
      <div class="ambient-orb ambient-orb-two"></div>
      <div class="ambient-orb ambient-orb-three"></div>
    </div>

    <transition name="scroll-top">
      <button v-show="showScrollTop" type="button"
        class="fixed bottom-5 right-5 z-50 grid h-11 w-11 place-items-center rounded-full border border-[var(--border)] bg-[var(--glass-strong)] text-[var(--text-strong)] shadow-[var(--shadow-soft)] backdrop-blur-2xl transition duration-300 hover:-translate-y-1 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--focus)]"
        aria-label="Voltar ao topo" title="Voltar ao topo" @click="scrollToTop">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8"
          class="h-5 w-5" aria-hidden="true">
          <path stroke-linecap="round" stroke-linejoin="round" d="M6 15l6-6 6 6" />
        </svg>
      </button>
    </transition>

    <!-- HERO -->
    <section class="relative z-10">
      <div class="mx-auto max-w-7xl px-5 pb-10 pt-20 sm:px-6 lg:px-8 lg:pt-28">
        <div class="mx-auto max-w-5xl text-center" data-reveal>
          <div
            class="inline-flex items-center gap-2 rounded-full border border-[var(--border)] bg-[var(--glass)] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[var(--muted)] shadow-[var(--shadow-soft)] backdrop-blur-2xl">
            <span class="relative flex h-2 w-2">
              <span
                class="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-40"></span>
              <span class="relative inline-flex h-2 w-2 rounded-full bg-emerald-500"></span>
            </span>
            Templates de soluções
            <span class="hidden text-[var(--muted-2)] sm:inline">/ Explore possibilities</span>
          </div>

          <h1
            class="mx-auto mt-7 max-w-5xl text-5xl font-semibold tracking-[-0.06em] text-[var(--text-strong)] sm:text-6xl lg:text-[5.2rem] leading-[1.06]">
            Veja antes de construir.
            <span class="hero-gradient block pb-2">
              Adapte depois ao seu negócio.
            </span>
          </h1>

          <p class="mx-auto mt-7 max-w-3xl text-lg leading-8 text-[var(--muted)] sm:text-xl">
            Explore modelos de sistemas, automações, dashboards, portais e produtos digitais.
            Cada exemplo é um ponto de partida, não um pacote fechado.
          </p>

          <div class="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <a :href="generalWhatsHref" target="_blank" rel="noopener"
              class="primary-button group inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold">
              Criar solução sob medida
              <span class="transition-transform duration-300 group-hover:translate-x-0.5" aria-hidden="true">→</span>
            </a>

            <router-link to="/projects"
              class="secondary-button inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold">
              Ver projetos reais
            </router-link>
          </div>
        </div>

        <!-- SHOWCASE -->
        <div class="relative mx-auto mt-16 max-w-6xl" data-reveal>
          <div class="hero-halo absolute inset-x-16 top-12 h-48 rounded-full blur-3xl"></div>

          <div class="showcase-frame relative rounded-[2.4rem] p-2.5 sm:p-3">
            <div
              class="overflow-hidden rounded-[1.9rem] border border-white/10 bg-[#08090b] text-white sm:rounded-[2.25rem]">
              <div class="flex items-center justify-between border-b border-white/[0.07] px-5 py-4 sm:px-6">
                <div class="flex gap-2">
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                  <span class="h-2.5 w-2.5 rounded-full bg-white/20"></span>
                </div>
                <div
                  class="hidden rounded-full bg-white/[0.06] px-4 py-2 text-[10px] font-semibold text-zinc-400 ring-1 ring-white/10 sm:block">
                  Grivaro / solution-library</div>
                <span class="rounded-full bg-white/[0.07] px-3 py-1.5 text-[10px] font-semibold text-zinc-300">12
                  modelos</span>
              </div>

              <div class="grid gap-3 p-5 sm:grid-cols-3 sm:p-6">
                <article v-for="item in heroPreview" :key="item.title" class="preview-card rounded-[1.5rem] p-4">
                  <div class="flex items-center justify-between">
                    <span
                      class="rounded-full bg-white/[0.07] px-2.5 py-1 text-[9px] font-semibold text-zinc-400 ring-1 ring-white/[0.08]">{{
                        item.categoryLabel }}</span>
                    <span class="text-zinc-600">↗</span>
                  </div>
                  <div class="mt-5 min-h-[8rem] rounded-[1.1rem] bg-white/[0.04] p-3 ring-1 ring-white/[0.06]">
                    <div class="grid h-full gap-2" :class="item.preview === 'pipeline' ? 'grid-cols-3' : 'grid-cols-2'">
                      <span v-for="n in item.preview === 'pipeline' ? 6 : 4" :key="n"
                        class="rounded-xl bg-white/[0.07]"></span>
                    </div>
                  </div>
                  <h2 class="mt-5 text-base font-semibold tracking-[-0.02em]">{{ item.title }}</h2>
                  <p class="mt-2 line-clamp-2 text-[10px] leading-5 text-zinc-500">{{ item.description }}</p>
                </article>
              </div>
            </div>
          </div>

          <!-- SEARCH DOCK -->
          <div
            class="search-dock relative z-20 mx-auto mt-6 w-[96%] rounded-[1.7rem] border border-[var(--border)] bg-[var(--glass-strong)] p-3 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:w-[92%]">
            <div class="grid gap-3 lg:grid-cols-[1fr_auto]">
              <label class="relative block">
                <span class="sr-only">Buscar um modelo de solução</span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                  stroke-width="1.8"
                  class="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--muted-2)]"
                  aria-hidden="true">
                  <circle cx="11" cy="11" r="7"></circle>
                  <path d="m20 20-3.5-3.5"></path>
                </svg>
                <input v-model="query" type="search" autocomplete="off"
                  placeholder="Buscar por CRM, financeiro, WhatsApp, dashboard..."
                  class="h-12 w-full rounded-full border border-[var(--border)] bg-[var(--surface)] pl-11 pr-5 text-sm font-medium text-[var(--text-strong)] outline-none placeholder:text-[var(--muted-2)] focus:border-[var(--text-strong)]" />
              </label>

              <div class="flex flex-wrap gap-2" aria-label="Filtrar templates por categoria">
                <button v-for="category in categories" :key="category.value" type="button"
                  :aria-pressed="activeCategory === category.value" @click="activeCategory = category.value" :class="[
                    'h-12 rounded-full px-4 text-xs font-semibold transition duration-300 sm:px-5',
                    activeCategory === category.value
                      ? 'bg-[var(--text-strong)] text-[var(--inverse)]'
                      : 'border border-[var(--border)] bg-[var(--surface)] text-[var(--muted)] hover:-translate-y-0.5 hover:text-[var(--text-strong)]'
                  ]">
                  {{ category.label }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="relative z-10 mx-auto mt-8 h-20 max-w-7xl px-5 sm:px-6 lg:px-8" aria-hidden="true">
      <span class="flow-line"></span><span class="flow-node"></span>
    </div>

    <!-- GALERIA -->
    <section class="relative z-10 py-16 sm:py-20">
      <div class="mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
        <div class="grid gap-10 lg:grid-cols-12 lg:items-end" data-reveal>
          <div class="lg:col-span-7">
            <p class="eyebrow">Galeria</p>
            <h2 class="section-title mt-4">Um ponto de partida visual <span class="text-[var(--muted-2)]">para cada
                necessidade.</span></h2>
          </div>
          <div class="lg:col-span-5">
            <p class="text-lg leading-8 text-[var(--muted)]">Os cards mostram formatos possíveis para acelerar a
              conversa e deixar tangível o que pode ser criado.</p>
            <p class="mt-4 text-xs font-semibold uppercase tracking-[0.14em] text-[var(--muted-2)]" aria-live="polite">
              {{ filteredTemplates.length }} {{ filteredTemplates.length === 1 ? 'modelo encontrado' : 'modelos encontrados' }}</p>
          </div>
        </div>

        <transition-group v-if="filteredTemplates.length" name="catalog" tag="div"
          class="mt-14 grid gap-5 md:grid-cols-2 xl:grid-cols-12 xl:items-start">
          <article v-for="(item, index) in filteredTemplates" :key="item.slug"
            :class="['template-card group min-w-0', templateSpan(index)]">
            <router-link :to="`/templates/${item.slug}`" :aria-label="`Abrir apresentação da solução ${item.title}`"
              class="block h-full overflow-hidden rounded-[2.15rem] border border-[var(--border)] bg-[var(--card)] backdrop-blur-xl transition duration-500 hover:-translate-y-1.5 hover:shadow-[var(--shadow-large)] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--focus)]">
              <div class="p-3 pb-0">
                <div class="overflow-hidden rounded-[1.65rem] bg-[#090a0c] p-4 text-white ring-1 ring-white/[0.06]">
                  <div class="flex items-center justify-between gap-4">
                    <div class="flex gap-1.5"><span class="h-2.5 w-2.5 rounded-full bg-white/20"></span><span
                        class="h-2.5 w-2.5 rounded-full bg-white/20"></span><span
                        class="h-2.5 w-2.5 rounded-full bg-white/20"></span></div>
                    <span class="rounded-full bg-white/10 px-3 py-1 text-[10px] font-semibold text-zinc-300">{{
                      item.previewLabel }}</span>
                  </div>

                  <div class="mt-5 grid min-h-[13rem] gap-3">
                    <template v-if="item.preview === 'dashboard'">
                      <div class="grid grid-cols-3 gap-2">
                        <div v-for="metric in item.metrics" :key="metric.label"
                          class="rounded-2xl bg-white/[0.07] p-3 ring-1 ring-white/10">
                          <p class="text-[10px] text-zinc-500">{{ metric.label }}</p>
                          <p class="mt-1 text-base font-semibold">{{ metric.value }}</p>
                        </div>
                      </div>
                      <div class="grid flex-1 grid-cols-[1.4fr_.8fr] gap-2">
                        <div class="flex items-end gap-1 rounded-2xl bg-white/[0.05] p-3 ring-1 ring-white/10"><span
                            v-for="(bar, barIndex) in item.bars" :key="barIndex" class="flex-1 rounded-t bg-white/70"
                            :style="{ height: `${bar}%` }"></span></div>
                        <div class="space-y-2 rounded-2xl bg-white/[0.05] p-3 ring-1 ring-white/10"><span
                            v-for="line in 4" :key="line" class="block h-2 rounded-full bg-white/10"
                            :class="line === 2 ? 'w-3/4' : line === 4 ? 'w-1/2' : 'w-full'"></span></div>
                      </div>
                    </template>

                    <template v-else-if="item.preview === 'pipeline'">
                      <div class="grid grid-cols-3 gap-2">
                        <div v-for="column in item.columns" :key="column"
                          class="rounded-2xl bg-white/[0.05] p-2.5 ring-1 ring-white/10">
                          <p class="mb-2 text-[10px] font-semibold text-zinc-400">{{ column }}</p>
                          <div v-for="card in 3" :key="card" class="mb-2 rounded-xl bg-white/[0.08] p-2 last:mb-0"><span
                              class="block h-2 w-3/4 rounded-full bg-white/25"></span><span
                              class="mt-2 block h-1.5 w-1/2 rounded-full bg-white/10"></span></div>
                        </div>
                      </div>
                    </template>

                    <template v-else-if="item.preview === 'chat'">
                      <div class="grid h-full grid-cols-[.55fr_1fr] gap-2">
                        <div class="rounded-2xl bg-white/[0.05] p-3 ring-1 ring-white/10"><span
                            class="block h-7 w-7 rounded-xl bg-white/15"></span>
                          <div class="mt-4 space-y-2"><span v-for="n in 5" :key="n"
                              class="block h-2 rounded-full bg-white/10"></span></div>
                        </div>
                        <div class="flex flex-col rounded-2xl bg-white/[0.05] p-3 ring-1 ring-white/10">
                          <div class="flex-1 space-y-2">
                            <div class="mr-10 rounded-2xl rounded-bl-md bg-white/10 p-3"><span
                                class="block h-2 w-4/5 rounded-full bg-white/20"></span><span
                                class="mt-2 block h-2 w-1/2 rounded-full bg-white/10"></span></div>
                            <div class="ml-10 rounded-2xl rounded-br-md bg-white p-3"><span
                                class="block h-2 w-4/5 rounded-full bg-zinc-300"></span><span
                                class="mt-2 block h-2 w-2/3 rounded-full bg-zinc-200"></span></div>
                          </div>
                          <div class="mt-3 h-9 rounded-full bg-white/10"></div>
                        </div>
                      </div>
                    </template>

                    <template v-else-if="item.preview === 'table'">
                      <div class="grid grid-cols-[.5fr_1.5fr] gap-2">
                        <div class="rounded-2xl bg-white/[0.05] p-3 ring-1 ring-white/10"><span
                            class="block h-8 w-8 rounded-xl bg-white/15"></span>
                          <div class="mt-4 space-y-3"><span v-for="n in 6" :key="n"
                              class="block h-2 rounded-full bg-white/10"></span></div>
                        </div>
                        <div class="rounded-2xl bg-white/[0.05] p-3 ring-1 ring-white/10">
                          <div class="grid grid-cols-3 gap-2"><span v-for="n in 3" :key="n"
                              class="h-10 rounded-xl bg-white/10"></span></div>
                          <div class="mt-3 space-y-2">
                            <div v-for="n in 5" :key="n"
                              class="grid grid-cols-[1.4fr_.7fr_.4fr] gap-2 rounded-xl bg-white/[0.04] p-2"><span
                                class="h-2 rounded-full bg-white/15"></span><span
                                class="h-2 rounded-full bg-white/10"></span><span
                                class="h-2 rounded-full bg-white/10"></span></div>
                          </div>
                        </div>
                      </div>
                    </template>

                    <template v-else-if="item.preview === 'calendar'">
                      <div class="grid h-full grid-cols-[1fr_.55fr] gap-2">
                        <div class="rounded-2xl bg-white/[0.05] p-3 ring-1 ring-white/10">
                          <div class="grid grid-cols-7 gap-1"><span v-for="day in 35" :key="day"
                              class="aspect-square rounded-md"
                              :class="day === 17 || day === 24 ? 'bg-white' : 'bg-white/[0.07]'"></span></div>
                        </div>
                        <div class="space-y-2 rounded-2xl bg-white/[0.05] p-3 ring-1 ring-white/10">
                          <div v-for="n in 4" :key="n" class="rounded-xl bg-white/[0.08] p-2"><span
                              class="block h-2 w-2/3 rounded-full bg-white/20"></span><span
                              class="mt-2 block h-1.5 w-1/2 rounded-full bg-white/10"></span></div>
                        </div>
                      </div>
                    </template>

                    <template v-else-if="item.preview === 'landing'">
                      <div class="flex min-h-[13rem] items-center justify-center">
                        <div
                          class="w-[72%] max-w-[14rem] overflow-hidden rounded-xl bg-white p-2.5 text-zinc-950 shadow-xl">
                          <div class="flex items-center justify-between"><span
                              class="h-2.5 w-10 rounded-full bg-zinc-200"></span>
                            <div class="flex gap-1.5"><span class="h-1.5 w-5 rounded-full bg-zinc-100"></span><span
                                class="h-1.5 w-5 rounded-full bg-zinc-100"></span><span
                                class="h-1.5 w-5 rounded-full bg-zinc-100"></span></div>
                          </div>
                          <div class="mx-auto mt-4 max-w-[85%] text-center"><span
                              class="mx-auto block h-1.5 w-14 rounded-full bg-zinc-200"></span><span
                              class="mx-auto mt-2.5 block h-3 w-full rounded-full bg-zinc-950"></span><span
                              class="mx-auto mt-1.5 block h-3 w-4/5 rounded-full bg-zinc-950"></span><span
                              class="mx-auto mt-3 block h-6 w-20 rounded-full bg-zinc-950"></span></div>
                          <div class="mt-4 grid grid-cols-3 gap-1.5"><span v-for="n in 3" :key="n"
                              class="h-8 rounded-lg bg-zinc-100"></span></div>
                        </div>
                      </div>
                    </template>

                    <template v-else>
                      <div class="grid h-full grid-cols-2 gap-2">
                        <div v-for="n in 4" :key="n" class="rounded-2xl bg-white/[0.05] p-4 ring-1 ring-white/10"><span
                            class="block h-8 w-8 rounded-xl bg-white/15"></span><span
                            class="mt-5 block h-2 w-3/4 rounded-full bg-white/20"></span><span
                            class="mt-2 block h-2 w-1/2 rounded-full bg-white/10"></span></div>
                      </div>
                    </template>
                  </div>
                </div>
              </div>

              <div class="p-7">
                <div class="flex flex-wrap items-center gap-2"><span
                    class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-xs font-semibold text-[var(--muted)]">{{
                      item.categoryLabel }}</span><span v-if="item.highlight"
                    class="rounded-full bg-[var(--text-strong)] px-3 py-1.5 text-xs font-semibold text-[var(--inverse)]">{{
                      item.highlight }}</span></div>
                <h3 class="mt-5 text-2xl font-semibold tracking-[-0.035em] text-[var(--text-strong)]">{{ item.title }}
                </h3>
                <p class="mt-3 text-sm leading-6 text-[var(--muted)]">{{ item.description }}</p>
                <div class="mt-6 flex flex-wrap gap-2"><span v-for="feature in item.features" :key="feature"
                    class="rounded-full border border-[var(--border)] bg-[var(--surface)] px-3 py-1.5 text-xs font-medium text-[var(--muted)]">{{
                      feature }}</span></div>
                <div class="mt-8 flex items-center justify-between gap-4 border-t border-[var(--border)] pt-5"><span
                    class="text-sm font-semibold text-[var(--text-strong)]">Ver apresentação</span><span
                    class="grid h-10 w-10 place-items-center rounded-full bg-[var(--text-strong)] text-[var(--inverse)] transition duration-300 group-hover:translate-x-1"
                    aria-hidden="true">→</span></div>
              </div>
            </router-link>
          </article>
        </transition-group>

        <div v-else
          class="mt-14 rounded-[2rem] border border-[var(--border)] bg-[var(--glass)] p-10 text-center shadow-[var(--shadow-soft)] backdrop-blur-2xl">
          <p class="text-lg font-semibold text-[var(--text-strong)]">Nenhum modelo encontrado.</p>
          <p class="mt-2 text-sm text-[var(--muted)]">Tente outro termo ou escolha “Todos”.</p>
          <button type="button" @click="clearFilters"
            class="secondary-button mt-6 inline-flex h-10 items-center justify-center rounded-full px-5 text-xs font-semibold">Limpar
            filtros</button>
        </div>
      </div>
    </section>

    <!-- SOB MEDIDA -->
    <div class="relative z-10 mx-auto max-w-7xl px-5 sm:px-6 lg:px-8" aria-hidden="true">
      <div class="curve-transition"><span class="curve-dot"></span></div>
    </div>

    <section class="relative z-10 py-14 sm:py-20">
      <div class="mx-auto max-w-[88rem] px-3 sm:px-5 lg:px-7">
        <div
          class="dark-panel relative overflow-hidden rounded-[2.7rem] px-5 py-16 text-white shadow-[var(--shadow-large)] sm:px-8 sm:py-20 lg:px-12 xl:px-16">
          <div class="dark-panel-grid pointer-events-none absolute inset-0"></div>
          <div class="dark-orb absolute -right-24 -top-28 h-96 w-96 rounded-full blur-3xl"></div>
          <div class="relative grid gap-12 lg:grid-cols-12 lg:items-center">
            <div class="lg:col-span-7" data-reveal>
              <p class="text-xs font-semibold uppercase tracking-[0.18em] text-zinc-500">Sob medida de verdade</p>
              <h2 class="mt-5 max-w-3xl text-4xl font-semibold tracking-[-0.05em] sm:text-5xl lg:text-6xl">O template
                mostra
                a direção. <span class="block text-zinc-500">A solução acompanha sua operação.</span></h2>
              <p class="mt-6 max-w-2xl text-lg leading-8 text-zinc-400">Podemos combinar modelos, integrar ferramentas
                existentes e criar módulos específicos sem forçar o negócio a caber em um sistema genérico.</p>
            </div>
            <div class="grid gap-3 lg:col-span-5">
              <article v-for="principle in principles" :key="principle.number"
                class="rounded-[1.5rem] bg-white/[0.055] p-5 ring-1 ring-white/10 backdrop-blur-xl transition duration-500 hover:translate-x-1 hover:bg-white/[0.085]"
                data-reveal>
                <div class="flex gap-4"><span class="text-xs font-bold text-zinc-600">{{ principle.number }}</span>
                  <div>
                    <h3 class="text-lg font-semibold">{{ principle.title }}</h3>
                    <p class="mt-2 text-sm leading-6 text-zinc-400">{{ principle.description }}</p>
                  </div>
                </div>
              </article>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="relative z-10 pb-24 pt-16 sm:pb-28 sm:pt-20">
      <div class="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8">
        <div
          class="cta-panel relative overflow-hidden rounded-[2.6rem] border border-[var(--border)] px-6 py-12 shadow-[var(--shadow-large)] backdrop-blur-2xl sm:px-9 sm:py-14 lg:px-12"
          data-reveal>
          <div class="cta-orb absolute -right-24 -top-24 h-72 w-72 rounded-full blur-3xl"></div>
          <div class="relative grid gap-10 lg:grid-cols-12 lg:items-center">
            <div class="lg:col-span-8">
              <p class="eyebrow">Próxima possibilidade</p>
              <h2
                class="mt-4 max-w-3xl text-4xl font-semibold tracking-[-0.05em] text-[var(--text-strong)] sm:text-5xl">
                Não
                encontrou exatamente o que precisa?</h2>
              <p class="mt-5 max-w-2xl text-lg leading-8 text-[var(--muted)]">Conte como sua operação funciona, onde
                está o
                gargalo e quais ferramentas já fazem parte do processo.</p>
            </div>
            <div class="flex flex-col gap-3 lg:col-span-4"><a :href="generalWhatsHref" target="_blank" rel="noopener"
                class="primary-button inline-flex h-12 items-center justify-center gap-2 rounded-full px-7 text-sm font-semibold">Falar
                no WhatsApp <span aria-hidden="true">→</span></a><router-link to="/solutions"
                class="secondary-button inline-flex h-12 items-center justify-center rounded-full px-7 text-sm font-semibold">Ver
                serviços</router-link></div>
          </div>
        </div>
      </div>
    </section>
  </main>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref } from 'vue'

const query = ref('')
const activeCategory = ref('all')
const showScrollTop = ref(false)
const pointer = reactive({ x: 50, y: 20 })
let observer = null

const phone = '77999631644'
const generalWhatsHref = `https://wa.me/${phone}?text=${encodeURIComponent('Olá! Quero conversar sobre uma solução digital sob medida para minha operação.')}`

const categories = [
  { value: 'all', label: 'Todos' },
  { value: 'management', label: 'Gestão' },
  { value: 'finance', label: 'Financeiro' },
  { value: 'service', label: 'Atendimento' },
  { value: 'ai', label: 'IA' },
  { value: 'integrations', label: 'Integrações' },
  { value: 'landing', label: 'Landing Pages' },
  { value: 'commerce', label: 'Comercial' }
]

const templates = [
  {
    slug: 'dashboard-financeiro',
    category: 'finance',
    categoryLabel: 'Financeiro',
    highlight: 'Popular',
    title: 'Dashboard financeiro',
    description:
      'Visão central de receitas, despesas, fluxo de caixa, indicadores e movimentações da operação.',
    features: ['Fluxo de caixa', 'DRE', 'Metas', 'Relatórios'],
    preview: 'dashboard',
    previewLabel: 'Finance OS',
    metrics: [
      { label: 'Receita', value: '128k' },
      { label: 'Margem', value: '31%' },
      { label: 'Caixa', value: '84k' }
    ],
    bars: [42, 68, 54, 82, 65, 92, 72, 100]
  },
  {
    slug: 'crm-pipeline',
    category: 'commerce',
    categoryLabel: 'Comercial',
    highlight: null,
    title: 'CRM e pipeline comercial',
    description:
      'Organize leads, propostas, negociações e follow-ups em um fluxo comercial simples de acompanhar.',
    features: ['Leads', 'Kanban', 'Propostas', 'Follow-up'],
    preview: 'pipeline',
    previewLabel: 'Sales Flow',
    columns: ['Novos', 'Proposta', 'Fechamento']
  },
  {
    slug: 'central-atendimento-whatsapp',
    category: 'service',
    categoryLabel: 'Atendimento',
    highlight: 'Automação',
    title: 'Central de atendimento',
    description:
      'Caixa de entrada organizada para WhatsApp, distribuição de conversas, histórico e automações.',
    features: ['WhatsApp', 'Filas', 'Histórico', 'Templates'],
    preview: 'chat',
    previewLabel: 'Inbox'
  },
  {
    slug: 'agente-ia',
    category: 'ai',
    categoryLabel: 'Inteligência artificial',
    highlight: 'IA',
    title: 'Agente de IA',
    description:
      'Agente conectado aos dados e processos da empresa para responder, executar tarefas e apoiar a equipe.',
    features: ['RAG', 'Ferramentas', 'Automação', 'Contexto'],
    preview: 'chat',
    previewLabel: 'AI Agent'
  },
  {
    slug: 'backoffice-operacional',
    category: 'management',
    categoryLabel: 'Gestão',
    highlight: null,
    title: 'Backoffice operacional',
    description:
      'Painel interno para cadastros, usuários, permissões, processos, relatórios e rotinas administrativas.',
    features: ['Usuários', 'Permissões', 'Cadastros', 'Relatórios'],
    preview: 'table',
    previewLabel: 'Operations'
  },
  {
    slug: 'agenda-consultorio',
    category: 'management',
    categoryLabel: 'Gestão',
    highlight: 'Consultórios',
    title: 'Agenda e gestão de atendimentos',
    description:
      'Agenda visual com clientes, profissionais, horários, status, lembretes e visão diária da operação.',
    features: ['Agenda', 'Clientes', 'Lembretes', 'Status'],
    preview: 'calendar',
    previewLabel: 'Schedule'
  },
  {
    slug: 'portal-cliente',
    category: 'service',
    categoryLabel: 'Atendimento',
    highlight: null,
    title: 'Portal do cliente',
    description:
      'Área autenticada para acompanhar solicitações, documentos, pagamentos, histórico e serviços.',
    features: ['Login', 'Documentos', 'Status', 'Pagamentos'],
    preview: 'portal',
    previewLabel: 'Client Portal'
  },
  {
    slug: 'landing-page',
    category: 'landing',
    categoryLabel: 'Landing Page',
    highlight: 'Conversão',
    title: 'Landing Page de alta conversão',
    description:
      'Página sob medida para apresentar uma oferta com clareza, fortalecer confiança e transformar visitas em contatos ou vendas.',
    features: ['Hero', 'Prova social', 'Oferta', 'CTA'],
    preview: 'landing',
    previewLabel: 'Conversion Page'
  },
  {
    slug: 'hub-integracoes',
    category: 'integrations',
    categoryLabel: 'Integrações',
    highlight: 'API',
    title: 'Hub de integrações',
    description:
      'Central para conectar APIs, webhooks, ERPs, CRMs, pagamentos e serviços externos com monitoramento.',
    features: ['APIs', 'Webhooks', 'Logs', 'Sincronização'],
    preview: 'portal',
    previewLabel: 'Integration Hub'
  },
  {
    slug: 'painel-executivo',
    category: 'management',
    categoryLabel: 'Gestão',
    highlight: null,
    title: 'Painel executivo',
    description:
      'Indicadores essenciais do negócio organizados para decisões rápidas, metas e acompanhamento gerencial.',
    features: ['KPIs', 'Metas', 'Indicadores', 'Comparativos'],
    preview: 'dashboard',
    previewLabel: 'Executive',
    metrics: [
      { label: 'Meta', value: '92%' },
      { label: 'NPS', value: '74' },
      { label: 'Crescimento', value: '+18%' }
    ],
    bars: [36, 52, 48, 66, 60, 78, 88, 96]
  },
  {
    slug: 'automacao-processos',
    category: 'integrations',
    categoryLabel: 'Integrações',
    highlight: 'No manual',
    title: 'Automação de processos',
    description:
      'Fluxos automáticos para tarefas repetitivas, notificações, sincronização de dados e regras operacionais.',
    features: ['Gatilhos', 'Regras', 'Alertas', 'Integrações'],
    preview: 'pipeline',
    previewLabel: 'Workflow',
    columns: ['Entrada', 'Processando', 'Concluído']
  },
  {
    slug: 'saas-mvp',
    category: 'ai',
    categoryLabel: 'Produto digital',
    highlight: 'MVP',
    title: 'SaaS / produto do zero',
    description:
      'Base completa para lançar um produto digital com autenticação, planos, painel, usuários e módulos evolutivos.',
    features: ['MVP', 'Login', 'Planos', 'Dashboard'],
    preview: 'portal',
    previewLabel: 'SaaS'
  }
]

const principles = [
  {
    number: '01',
    title: 'Misture módulos',
    description:
      'Um dashboard financeiro pode ter CRM, WhatsApp, agenda e automações no mesmo ecossistema.'
  },
  {
    number: '02',
    title: 'Use seu fluxo real',
    description:
      'A interface e as regras acompanham o processo que a empresa já utiliza ou deseja implementar.'
  },
  {
    number: '03',
    title: 'Evolua por etapas',
    description:
      'Comece com o núcleo essencial e adicione novos módulos à medida que a operação precisar.'
  }
]

const heroPreview = computed(() => templates.slice(0, 3))

const filteredTemplates = computed(() => {
  const term = query.value.trim().toLowerCase()
  return templates.filter(item => {
    const matchesCategory = activeCategory.value === 'all' || item.category === activeCategory.value
    const haystack = [item.title, item.description, item.categoryLabel, ...item.features].join(' ').toLowerCase()
    return matchesCategory && (!term || haystack.includes(term))
  })
})

function templateSpan(index) {
  const layout = [
    'xl:col-span-7',
    'xl:col-span-5 xl:mt-10',
    'xl:col-span-5 xl:-mt-2',
    'xl:col-span-7 xl:mt-8',
    'xl:col-span-6',
    'xl:col-span-6 xl:mt-8',
    'xl:col-span-7',
    'xl:col-span-5 xl:mt-10',
    'xl:col-span-12'
  ]
  return layout[index % layout.length]
}

function clearFilters() {
  query.value = ''
  activeCategory.value = 'all'
}

function handlePointerMove(event) {
  const width = window.innerWidth || 1
  const height = window.innerHeight || 1
  pointer.x = Math.max(0, Math.min(100, (event.clientX / width) * 100))
  pointer.y = Math.max(0, Math.min(100, (event.clientY / height) * 100))
}

function handleScroll() {
  showScrollTop.value = window.scrollY > 520
}

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function setupReveal() {
  const elements = document.querySelectorAll('[data-reveal]')
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    elements.forEach(element => element.classList.add('is-visible'))
    return
  }
  observer = new IntersectionObserver(entries => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible')
        observer?.unobserve(entry.target)
      }
    })
  }, { threshold: 0.1, rootMargin: '0px 0px -45px 0px' })
  elements.forEach(element => observer.observe(element))
}

onMounted(() => {
  setupReveal()
  handleScroll()
  window.addEventListener('scroll', handleScroll, { passive: true })
})

onBeforeUnmount(() => {
  observer?.disconnect()
  window.removeEventListener('scroll', handleScroll)
})
</script>

<style scoped>
:global(:root) {
  --page: #f5f5f7;
  --surface: rgba(255, 255, 255, .90);
  --card: rgba(255, 255, 255, .76);
  --chip: rgba(245, 245, 247, .92);
  --glass: rgba(255, 255, 255, .54);
  --glass-strong: rgba(255, 255, 255, .84);
  --text: #27272a;
  --text-strong: #09090b;
  --muted: #66666d;
  --muted-2: #a1a1aa;
  --inverse: #fff;
  --border: rgba(24, 24, 27, .085);
  --focus: rgba(113, 113, 122, .18);
  --shadow-soft: 0 12px 38px rgba(24, 24, 27, .07);
  --shadow-large: 0 32px 100px rgba(24, 24, 27, .105);
  --hero-a: #0a0a0b;
  --hero-b: #7c7f87;
  --orb-one: rgba(147, 161, 185, .22);
  --orb-two: rgba(203, 213, 225, .31);
  --orb-three: rgba(226, 232, 240, .28);
  --cta: rgba(255, 255, 255, .92);
}

:global(html[data-theme='dark']) {
  --page: #08090b;
  --surface: rgba(15, 16, 19, .90);
  --card: rgba(19, 20, 24, .76);
  --chip: rgba(26, 27, 31, .92);
  --glass: rgba(19, 20, 23, .54);
  --glass-strong: rgba(18, 19, 22, .84);
  --text: #e4e4e7;
  --text-strong: #fafafa;
  --muted: #a1a1aa;
  --muted-2: #5f6169;
  --inverse: #09090b;
  --border: rgba(255, 255, 255, .085);
  --focus: rgba(212, 212, 216, .13);
  --shadow-soft: 0 12px 42px rgba(0, 0, 0, .30);
  --shadow-large: 0 36px 120px rgba(0, 0, 0, .50);
  --hero-a: #fff;
  --hero-b: #777a84;
  --orb-one: rgba(71, 85, 105, .23);
  --orb-two: rgba(63, 63, 70, .28);
  --orb-three: rgba(39, 39, 42, .28);
  --cta: rgba(18, 19, 22, .94);
}

:global(html) {
  background: var(--page);
  transition: background-color 350ms ease
}

.site-page {
  transition: background-color 350ms ease, color 350ms ease
}

.ambient-grid {
  opacity: .31;
  background-image: linear-gradient(to right, var(--border) 1px, transparent 1px), linear-gradient(to bottom, var(--border) 1px, transparent 1px);
  background-size: 64px 64px;
  mask-image: linear-gradient(to bottom, black, transparent 82%)
}

.ambient-noise {
  opacity: .021;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.85' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.85'/%3E%3C/svg%3E")
}

.pointer-glow {
  background: radial-gradient(42rem circle at var(--pointer-x) var(--pointer-y), var(--orb-one), transparent 68%);
  transition: background 120ms linear
}

.ambient-orb {
  position: absolute;
  border-radius: 9999px;
  filter: blur(95px);
  will-change: transform
}

.ambient-orb-one {
  left: -11rem;
  top: 8rem;
  width: 34rem;
  height: 34rem;
  background: var(--orb-one);
  animation: float-a 14s ease-in-out infinite
}

.ambient-orb-two {
  right: -12rem;
  top: 48rem;
  width: 38rem;
  height: 38rem;
  background: var(--orb-two);
  animation: float-b 18s ease-in-out infinite
}

.ambient-orb-three {
  left: 28%;
  top: 125rem;
  width: 30rem;
  height: 30rem;
  background: var(--orb-three);
  animation: float-c 21s ease-in-out infinite
}

.hero-gradient {
  background: linear-gradient(120deg, var(--hero-a) 5%, var(--hero-b) 96%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent
}

.hero-halo,
.cta-orb {
  background: var(--orb-one)
}

.primary-button {
  color: var(--inverse);
  background: var(--text-strong);
  box-shadow: 0 8px 24px rgba(0, 0, 0, .10), inset 0 1px 0 rgba(255, 255, 255, .12);
  transition: transform 300ms ease, box-shadow 300ms ease
}

.primary-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 32px rgba(0, 0, 0, .16), inset 0 1px 0 rgba(255, 255, 255, .13)
}

.secondary-button {
  color: var(--text-strong);
  background: var(--glass-strong);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-soft);
  backdrop-filter: blur(18px);
  transition: transform 300ms ease, border-color 300ms ease
}

.secondary-button:hover {
  transform: translateY(-2px);
  border-color: color-mix(in srgb, var(--text-strong) 18%, transparent)
}

.showcase-frame {
  background: linear-gradient(135deg, color-mix(in srgb, var(--text-strong) 16%, transparent), color-mix(in srgb, var(--text-strong) 4%, transparent));
  border: 1px solid var(--border);
  box-shadow: var(--shadow-large), inset 0 1px 0 rgba(255, 255, 255, .10);
  backdrop-filter: blur(18px)
}

.preview-card {
  background: rgba(255, 255, 255, .045);
  border: 1px solid rgba(255, 255, 255, .075);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .025)
}

.search-dock {
  transition: transform 400ms ease, box-shadow 400ms ease
}

.search-dock:focus-within {
  transform: translateY(-2px);
  box-shadow: var(--shadow-large)
}

.flow-line {
  position: absolute;
  left: 50%;
  top: 0;
  width: 1px;
  height: 100%;
  background: linear-gradient(to bottom, transparent, var(--border) 25%, var(--border) 75%, transparent)
}

.flow-node {
  position: absolute;
  left: 50%;
  top: 50%;
  width: 9px;
  height: 9px;
  border: 2px solid var(--page);
  border-radius: 9999px;
  background: var(--text-strong);
  box-shadow: 0 0 0 5px color-mix(in srgb, var(--text-strong) 8%, transparent);
  transform: translate(-50%, -50%)
}

.eyebrow {
  font-size: .72rem;
  line-height: 1rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .18em;
  color: var(--muted)
}

.section-title {
  max-width: 52rem;
  font-size: clamp(2.35rem, 4.7vw, 4rem);
  line-height: 1.02;
  font-weight: 600;
  letter-spacing: -.055em;
  color: var(--text-strong)
}

.template-card {
  transition: transform 500ms ease
}

.curve-transition {
  position: relative;
  width: 100%;
  height: 5.5rem;
  overflow: hidden
}

.curve-transition::before {
  content: '';
  position: absolute;
  left: 12%;
  top: 50%;
  width: 76%;
  height: 5rem;
  border-top: 1px solid var(--border);
  border-radius: 50%;
  transform: translateY(18%)
}

.curve-dot {
  position: absolute;
  left: 50%;
  top: 2.25rem;
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: var(--text-strong);
  transform: translateX(-50%)
}

.dark-panel {
  background: radial-gradient(circle at 85% 20%, rgba(255, 255, 255, .07), transparent 30%), #090a0c;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .08), 0 36px 110px rgba(0, 0, 0, .22)
}

.dark-panel-grid {
  opacity: .31;
  background-image: linear-gradient(to right, rgba(255, 255, 255, .055) 1px, transparent 1px), linear-gradient(to bottom, rgba(255, 255, 255, .055) 1px, transparent 1px);
  background-size: 48px 48px;
  mask-image: linear-gradient(to left, black, transparent 74%)
}

.dark-orb {
  background: rgba(255, 255, 255, .07)
}

.cta-panel {
  background: linear-gradient(135deg, var(--cta), color-mix(in srgb, var(--cta) 85%, var(--page)))
}

[data-reveal] {
  opacity: 0;
  transform: translateY(26px) scale(.992);
  transition: opacity 780ms cubic-bezier(.22, .82, .22, 1), transform 780ms cubic-bezier(.22, .82, .22, 1)
}

[data-reveal].is-visible {
  opacity: 1;
  transform: translateY(0) scale(1)
}

.catalog-enter-active,
.catalog-leave-active,
.catalog-move {
  transition: opacity 350ms ease, transform 350ms ease
}

.catalog-enter-from,
.catalog-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(.98)
}

.scroll-top-enter-active,
.scroll-top-leave-active {
  transition: opacity .25s ease, transform .25s ease
}

.scroll-top-enter-from,
.scroll-top-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(.92)
}

@keyframes float-a {

  0%,
  100% {
    transform: translate3d(0, 0, 0)
  }

  50% {
    transform: translate3d(3rem, 2rem, 0)
  }
}

@keyframes float-b {

  0%,
  100% {
    transform: translate3d(0, 0, 0)
  }

  50% {
    transform: translate3d(-3rem, -2rem, 0)
  }
}

@keyframes float-c {

  0%,
  100% {
    transform: translate3d(0, 0, 0)
  }

  50% {
    transform: translate3d(1rem, -3rem, 0)
  }
}

@media(max-width:640px) {
  .ambient-grid {
    background-size: 44px 44px
  }

  .curve-transition::before {
    left: 2%;
    width: 96%
  }
}

@media(prefers-reduced-motion:reduce) {

  *,
  *::before,
  *::after {
    scroll-behavior: auto !important;
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: .01ms !important
  }

  [data-reveal] {
    opacity: 1;
    transform: none
  }
}
</style>
